import { UserManager, User } from 'oidc-client-ts'
import { OIDC_CONFIG } from './auth-config'
import { sanitizeInternalPath } from '../utils/safe-redirect'

class AuthService {
  private userManager: UserManager
  private storageKey: string
  private isRedirecting: boolean = false

  constructor() {
    this.userManager = new UserManager(OIDC_CONFIG)
    this.storageKey = `oidc.user:${OIDC_CONFIG.authority}:${OIDC_CONFIG.client_id}`

    this.userManager.events.addUserLoaded(() => {
      if (window.location.pathname.includes('callback')) {
        const redirectUri = sanitizeInternalPath(sessionStorage.getItem('redirectUri'))
        sessionStorage.removeItem('redirectUri')
        window.location.replace(redirectUri)
      }
    })

    this.userManager.events.addSilentRenewError(error => {
      console.error('Silent renew error:', error)
    })

    this.userManager.events.addAccessTokenExpired(() => {
      sessionStorage.clear()
    })

    this.userManager.events.addUserSignedOut(() => {
      sessionStorage.clear()
    })
  }

  async signinRedirect(): Promise<void> {
    if (this.isRedirecting) {
      return
    }

    this.isRedirecting = true

    if (window.location.pathname !== '/callback') {
      sessionStorage.setItem('redirectUri', window.location.pathname)
    }

    try {
      await this.userManager.signinRedirect()
    } catch (error) {
      console.error('Signin redirect error:', error)
      this.isRedirecting = false
      throw error
    }
  }

  async signinRedirectCallback(): Promise<User> {
    return await this.userManager.signinRedirectCallback()
  }

  async signoutRedirect(): Promise<void> {
    try {
      await this.userManager.signoutRedirect()
    } catch (error) {
      console.error('Signout redirect error:', error)
      try {
        await this.userManager.removeUser()
      } catch (e) {
        console.error('Error removing user:', e)
      }
      sessionStorage.clear()
      window.location.href = '/'
    }
  }

  async getUser(): Promise<User | null> {
    return await this.userManager.getUser()
  }

  getAccessToken(): string | undefined {
    const oidcStorage = sessionStorage.getItem(this.storageKey)
    if (!oidcStorage) return undefined
    const parsed = JSON.parse(oidcStorage)
    return parsed?.access_token
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken()
    if (!token) return false

    try {
      const payload = JSON.parse(atob(token.split('.')[1] ?? ''))
      const isExpired = payload.exp < Date.now() / 1000
      return !isExpired
    } catch {
      return false
    }
  }

  getUserManager(): UserManager {
    return this.userManager
  }
}

export const authService = new AuthService()
